import { useEffect, useMemo, useState } from 'react';
import type { I18nKey } from '@brao/shared';
import { ApiError } from '../api';
import { skillAgentsApi, type AgentTaskBrief, type RunOutcome, type SkillAgent, type SkillAgentDetail, type SkillAgentStatus } from '../api/skillAgents';
import { Table, type Column } from '../components/Table';
import { t } from '../i18n';
import { Link } from '../router';
import { fmtWhen, statusLabel } from './tasks.format';
import './agents.css';

/**
 * Agents → «За підпискою» (ADR-0011, mockup docs/mockups/phase-3/agents.html): the skill agents that
 * run on the human's Claude subscription, with their PLAYBOOK step, last run, tasks and questions. A
 * row opens the agent's card (`?agent=<slug>`), as dialogs and the inbox do.
 */

/** Literal keys, not interpolated ones: the i18n guard only sees plain string literals. */
const STEP_KEY: Record<number, I18nKey> = {
  0: 'skillAgents.step.0',
  1: 'skillAgents.step.1',
  2: 'skillAgents.step.2',
  3: 'skillAgents.step.3',
  4: 'skillAgents.step.4',
  5: 'skillAgents.step.5',
  6: 'skillAgents.step.6',
  7: 'skillAgents.step.7',
  8: 'skillAgents.step.8',
  9: 'skillAgents.step.9',
  10: 'skillAgents.step.10',
  11: 'skillAgents.step.11',
};
const STATUS_KEY: Record<SkillAgentStatus, I18nKey> = { learning: 'skillAgents.status.learning', working: 'skillAgents.status.working', paused: 'skillAgents.status.paused' };
export const OUTCOME_KEY: Record<RunOutcome, I18nKey> = {
  done: 'skillAgents.outcome.done',
  needs_human: 'skillAgents.outcome.needs_human',
  nothing: 'skillAgents.outcome.nothing',
  failed: 'skillAgents.outcome.failed',
};

export const stepText = (n: number) => t('skillAgents.step', { n, name: STEP_KEY[n] ? t(STEP_KEY[n]) : '' });
export const outcomeBadge = (o: RunOutcome) => <span className={o === 'done' ? 'badge ok' : o === 'failed' ? 'badge danger' : o === 'needs_human' ? 'badge warn' : 'badge'}>{t(OUTCOME_KEY[o])}</span>;

function agentFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get('agent');
}

function errorText(e: unknown): string {
  return e instanceof ApiError ? e.code : e instanceof Error ? e.message : t('common.error');
}

export function SkillAgents() {
  const [rows, setRows] = useState<SkillAgent[] | null>(null);
  const [selected, setSelected] = useState<string | null>(agentFromUrl);
  const [detail, setDetail] = useState<SkillAgentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    skillAgentsApi.list().then(setRows).catch((e) => setError(errorText(e)));
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (selected) url.searchParams.set('agent', selected);
    else url.searchParams.delete('agent');
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    setDetail(null);
    if (selected) skillAgentsApi.get(selected).then(setDetail).catch((e) => setError(errorText(e)));
  }, [selected]);

  const columns = useMemo<Column<SkillAgent>[]>(
    () => [
      {
        key: 'agent',
        title: t('skillAgents.col.agent'),
        wrap: true,
        sortValue: (a) => a.name,
        render: (a) => (
          <>
            <button type="button" className="row-select" style={{ all: 'unset', cursor: 'pointer', color: 'var(--c-link, inherit)' }} onClick={() => setSelected(a.slug)}>
              {a.name}
            </button>
            <br />
            <span className="muted">{a.role}</span>
          </>
        ),
      },
      {
        key: 'step',
        title: t('skillAgents.col.step'),
        wrap: true,
        sortValue: (a) => a.playbookStep,
        render: (a) => (
          <>
            {stepText(a.playbookStep)}
            <br />
            <span className="muted">{a.skillVersion ? t('skillAgents.skillVersion', { version: a.skillVersion }) : t('skillAgents.noSkill')}</span>
          </>
        ),
      },
      { key: 'status', title: t('skillAgents.col.status'), render: (a) => <span className={a.status === 'working' ? 'badge ok' : 'badge'}>{t(STATUS_KEY[a.status])}</span> },
      {
        key: 'lastRun',
        title: t('skillAgents.col.lastRun'),
        sortValue: (a) => a.lastRun?.finishedAt ?? '',
        render: (a) =>
          a.lastRun ? (
            <>
              {outcomeBadge(a.lastRun.outcome)} <span className="muted">{fmtWhen(a.lastRun.finishedAt)}</span>
            </>
          ) : (
            <span className="muted">{t('skillAgents.neverRan')}</span>
          ),
      },
      { key: 'tasks', title: t('skillAgents.col.tasks'), sortValue: (a) => a.openTasks, render: (a) => <span className="num">{a.openTasks}</span> },
      { key: 'questions', title: t('skillAgents.col.questions'), sortValue: (a) => a.questions, render: (a) => <span className={a.questions > 0 ? 'badge warn' : 'num'}>{a.questions}</span> },
    ],
    [],
  );

  return (
    <div className="skill-agents">
      <p className="muted">{t('skillAgents.intro')}</p>
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}
      {detail && <Card agent={detail} onClose={() => setSelected(null)} />}
      <div className="agent-table">
        <Table columns={columns} rows={rows ?? []} rowKey={(a) => a.id} empty={rows === null ? t('common.loading') : t('skillAgents.empty')} />
      </div>
    </div>
  );
}

function TaskList({ tasks, empty }: { tasks: AgentTaskBrief[]; empty: string }) {
  if (tasks.length === 0) return <p className="muted">{empty}</p>;
  return (
    <ul className="dash-list">
      {tasks.map((task) => (
        <li key={task.id}>
          <span className="key">{task.key}</span>
          <Link to={`/tasks/${task.id}`}>{task.title}</Link>
          <span className="meta">{statusLabel(task.status as Parameters<typeof statusLabel>[0])}</span>
        </li>
      ))}
    </ul>
  );
}

function Card({ agent, onClose }: { agent: SkillAgentDetail; onClose: () => void }) {
  const facts: Array<[I18nKey, string | null]> = [
    ['skillAgents.card.role', agent.role],
    ['skillAgents.card.business', agent.business.name],
    ['skillAgents.card.folder', agent.folder],
    ['skillAgents.card.pools', agent.pools.join(', ') || null],
    ['skillAgents.card.tags', agent.knowledgeTags.join(', ') || null],
    ['skillAgents.card.model', agent.model],
    ['skillAgents.card.schedule', agent.schedule],
    ['skillAgents.card.note', agent.note],
  ];
  return (
    <section className="card skill-agent-card" style={{ margin: 'var(--sp-4) 0' }} aria-label={agent.name}>
      <div className="row between">
        <h2 style={{ margin: 0 }}>{agent.name}</h2>
        <button type="button" className="btn secondary small" onClick={onClose}>{t('skillAgents.card.close')}</button>
      </div>
      <p className="muted">
        {stepText(agent.playbookStep)} · {agent.skillVersion ? t('skillAgents.skillVersion', { version: agent.skillVersion }) : t('skillAgents.noSkill')} · {t(STATUS_KEY[agent.status])}
      </p>
      <dl className="skill-agent-facts">
        {facts
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k}>
              <dt>{t(k)}</dt>
              <dd>{v}</dd>
            </div>
          ))}
      </dl>
      {agent.knowledgeTags.length > 0 && (
        <p>
          <Link to="/knowledge">{t('skillAgents.card.extracts')}</Link>
        </p>
      )}
      <h3>{t('skillAgents.card.tasks')}</h3>
      <TaskList tasks={agent.tasks} empty={t('skillAgents.card.noTasks')} />
      <h3>{t('skillAgents.card.questions')}</h3>
      <TaskList tasks={agent.openQuestions} empty={t('skillAgents.card.noQuestions')} />
      <h3>{t('skillAgents.card.runs')}</h3>
      {agent.runs.length === 0 && <p className="muted">{t('skillAgents.card.noRuns')}</p>}
      <ul className="dash-list skill-agent-runs">
        {agent.runs.map((r) => (
          <li key={r.id}>
            {outcomeBadge(r.outcome)}
            <span className="muted">{fmtWhen(r.finishedAt)}</span>
            {r.task && <Link to={`/tasks/${r.task.id}`}>{r.task.key}</Link>}
            {r.dialog && <Link to={`/dialogs?id=${encodeURIComponent(r.dialog.id)}`}>{t('skillAgents.card.dialog')}</Link>}
            <span className="run-summary">{r.summary}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
